import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

interface Macros { kcal: number; proteinG: number; carbG: number; fatG: number }
interface PerServing extends Macros { grams: number }

interface Item {
  id: string; foodId: string; name: string; brand: string | null;
  unit: string; grams: number; note: string | null;
  kcal: number; proteinG: number; carbG: number; fatG: number;
}

interface Unmatched { id: string; lineText: string; guessG: number | null }

interface Recipe {
  id: string; name: string; slot: string | null; serves: number; isMeal: boolean;
  methodMd: string | null; notes: string | null; prepMinutes: number | null;
  sourceUrl: string | null; items: Item[]; unmatched: Unmatched[];
  rawG: number; yieldG: number | null; portionBasisG: number; yieldMeasured: boolean;
  totals: Macros; perServing: PerServing; per100: Macros;
}

interface DraftMatch { foodId: string; name: string; brand: string | null; source: string; confidence: string }
interface DraftItem {
  original: string; searchTerm: string; grams: number; note: string | null;
  match: DraftMatch; alternatives: Array<{ foodId: string; name: string; brand: string | null }>;
}
interface Draft {
  name: string; sourceUrl: string; serves: number; slot: string | null;
  methodMd: string | null; prepMinutes: number | null;
  items: DraftItem[];
  unmatched: Array<{ lineText: string; guessG: number | null; reason: string }>;
  warning: string;
}

const SLOTS = ['breakfast', 'shake', 'lunch', 'snack', 'dinner'] as const;
const n1 = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));

export function Recipes(): JSX.Element {
  const [kind, setKind] = useState<'all' | 'meals' | 'recipes'>('all');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [open, setOpen] = useState<Recipe | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [importing, setImporting] = useState(false);
  const [servings, setServings] = useState('1');
  const [slot, setSlot] = useState<(typeof SLOTS)[number]>('dinner');

  const load = useCallback(async (): Promise<void> => {
    const r = await api<{ recipes: Recipe[] }>(`/recipes?kind=${kind}`);
    setRecipes(r.recipes);
  }, [kind]);

  useEffect(() => { void load(); }, [load]);

  async function importUrl(): Promise<void> {
    if (!url.trim()) return;
    setImporting(true);
    setMsg(null);
    try {
      setDraft(await api<Draft>('/recipes/import', { method: 'POST', body: JSON.stringify({ url: url.trim() }) }));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'That page could not be imported');
      setDraft(null);
    } finally {
      setImporting(false);
    }
  }

  /** Nothing is stored until this runs, which is the whole point of a draft. */
  async function saveDraft(d: Draft): Promise<void> {
    await api('/recipes', {
      method: 'POST',
      body: JSON.stringify({
        name: d.name, slot: d.slot, serves: d.serves, isMeal: false,
        methodMd: d.methodMd, prepMinutes: d.prepMinutes, sourceUrl: d.sourceUrl,
        items: d.items.map((i) => ({ foodId: i.match.foodId, grams: i.grams, note: i.note })),
      }),
    });
    setDraft(null);
    setUrl('');
    setMsg(`Saved "${d.name}"`);
    await load();
  }

  async function logPortion(r: Recipe): Promise<void> {
    const s = Number(servings);
    if (!(s > 0)) { setMsg('How many servings?'); return; }
    const res = await api<{ grams: number; basis: string }>(`/recipes/${r.id}/log`, {
      method: 'POST',
      body: JSON.stringify({ servings: s, mealSlot: slot }),
    });
    setMsg(
      `Logged ${Math.round(res.grams)} g to ${slot}`
      + (res.basis === 'cooked_weight' ? '' : ' — estimated from raw ingredient weight'),
    );
  }

  async function remove(r: Recipe): Promise<void> {
    try {
      await api(`/recipes/${r.id}`, { method: 'DELETE' });
      setOpen(null);
      await load();
    } catch {
      setMsg('You have eaten this before — removing it would change days you have already logged.');
    }
  }

  return (
    <>
      <div className="card">
        <h2>Recipes and meals</h2>
        <div className="row wrap small">
          {(['all', 'meals', 'recipes'] as const).map((k) => (
            <button key={k} type="button" className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
              {k === 'all' ? 'All' : k === 'meals' ? 'Meals' : 'Cooked'}
            </button>
          ))}
        </div>
        {msg && <p className="muted small">{msg}</p>}

        {recipes.length === 0 && <p className="muted small">Nothing saved yet.</p>}
        <table className="tbl">
          <tbody>
            {recipes.map((r) => (
              <tr key={r.id}>
                <td>
                  <button type="button" onClick={() => setOpen(open?.id === r.id ? null : r)}>
                    {r.name}
                  </button>
                  <div className="muted small">
                    {r.isMeal ? 'Meal' : 'Recipe'} · {r.items.length} ingredients
                    {r.serves > 1 ? ` · serves ${r.serves}` : ''}
                    {r.yieldMeasured ? ' · weighed cooked' : ' · raw weight only'}
                  </div>
                </td>
                <td className="muted small">
                  {Math.round(r.perServing.kcal)} kcal / serving<br />
                  P{n1(r.perServing.proteinG)} C{n1(r.perServing.carbG)} F{n1(r.perServing.fatG)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="card">
          <h2>{open.name}</h2>
          <p className="muted small">
            {open.yieldMeasured
              ? `Weighed after cooking: ${Math.round(open.yieldG!)} g from ${Math.round(open.rawG)} g of ingredients.`
              : `Never weighed after cooking, so portions come off the ${Math.round(open.rawG)} g of raw `
                + 'ingredients. Weigh the pot once and every portion after that is exact.'}
          </p>
          <p className="muted small">
            One serving is {Math.round(open.perServing.grams)} g · {Math.round(open.perServing.kcal)} kcal ·
            {' '}P{n1(open.perServing.proteinG)} C{n1(open.perServing.carbG)} F{n1(open.perServing.fatG)}
          </p>

          <div className="row">
            <input className="tiny" inputMode="decimal" value={servings} onChange={(e) => setServings(e.target.value)} />
            <select value={slot} onChange={(e) => setSlot(e.target.value as typeof slot)}>
              {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="button" onClick={() => void logPortion(open)}>Log this portion</button>
            <button type="button" onClick={() => void remove(open)}>Delete</button>
          </div>

          <table className="rows">
            <tbody>
              {open.items.map((i) => (
                <tr key={i.id}>
                  <td>{i.name}{i.note ? <span className="muted"> — {i.note}</span> : null}</td>
                  <td className="dim">{n1(i.grams)}{i.unit}</td>
                  <td className="dim">{Math.round(i.kcal)} kcal</td>
                </tr>
              ))}
            </tbody>
          </table>

          {open.unmatched.length > 0 && (
            <>
              <h3>Not counted</h3>
              <p className="muted small">
                These lines match nothing in the South African database, so none of their energy is
                in the numbers above.
              </p>
              <ul className="muted small">
                {open.unmatched.map((u) => <li key={u.id}>{u.lineText}</li>)}
              </ul>
            </>
          )}

          {open.methodMd && (
            <>
              <h3>Method</h3>
              <pre className="muted small" style={{ whiteSpace: 'pre-wrap' }}>{open.methodMd}</pre>
            </>
          )}
        </div>
      )}

      <div className="card">
        <h2>Import from a page</h2>
        <p className="muted small">
          Reads the recipe off the page, estimates a weight for each line and matches it to the
          South African food database. Nothing is stored until you save it.
        </p>
        <div className="row">
          <input placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
          <button type="button" disabled={importing} onClick={() => void importUrl()}>
            {importing ? 'Reading…' : 'Import'}
          </button>
        </div>

        {draft && (
          <>
            <h3>{draft.name}</h3>
            <p className="muted small">{draft.warning}</p>
            <p className="muted small">
              Serves {draft.serves}{draft.prepMinutes ? ` · ${draft.prepMinutes} min` : ''}
            </p>
            <table className="rows">
              <tbody>
                {draft.items.map((i) => (
                  <tr key={i.original}>
                    <td>{i.original}</td>
                    <td className="dim">{i.grams} g</td>
                    <td>
                      {i.match.name}{' '}
                      <span className={`chip chip-${i.match.confidence}`}>{i.match.confidence}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {draft.unmatched.length > 0 && (
              <>
                <h4>Could not be identified</h4>
                <ul className="muted small">
                  {draft.unmatched.map((u) => <li key={u.lineText}>{u.lineText} — {u.reason}</li>)}
                </ul>
              </>
            )}
            <div className="row">
              <button type="button" onClick={() => void saveDraft(draft)}>Save this recipe</button>
              <button type="button" onClick={() => setDraft(null)}>Discard</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
